<template>
  <div class="field no-select">
    <label
      v-if="label"
      class="mb-2 label no-select"
    >
      <h3>{{ label }}</h3>
    </label>
    <p
      v-if="hasHelpText"
      class="help-text"
      v-html="finalHelpText"
    ></p>
    <button
      type="button"
      class="btn-edit-image mt-2"
      :class="{ 'is-square': isSquare }"
      :title="buttonLabel"
      :style="{ background }"
      :disabled="isDisabled"
      @click="handleModal"
    >
      <IconEdit
        class="btn-edit-image__icon text-white"
        size="xs"
        aria-hidden="true"
        focusable="false"
      />
      <span class="sr-only">{{ buttonLabel }}</span>
    </button>
    <button
      v-show="showRemovalButton"
      type="button"
      class="btn-remove-image mt-1"
      :disabled="isDisabled"
      @click="removeImage"
    >
      Remover imagem
    </button>
    <div
      v-if="isModalOpen"
      class="storage-modal"
      @click.self="closeStorageModal"
    >
      <Storage @select="selectMedia">
        <template #header>
          <IconCollections size="sm" />
          <h2>Banco de imagens</h2>
          <button
            class="btn btn-secondary btn-white upload-btn"
            @click="openUploader"
          >
            Adicionar nova imagem
          </button>
          <button
            class="close"
            @click="closeStorageModal"
          >
            <IconClose
              size="xs"
              color="#000"
            />
          </button>
        </template>
      </Storage>
    </div>
  </div>
</template>

<script>
import { Crop, Local, pt, Uppload, xhrUploader } from 'uppload';

import getToken from '@/helpers/getToken';
import transparencyBackground from '@/assets/transparency.png';
import IconEdit from '@/components/icons/IconEdit.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconCollections from '@/components/icons/IconCollections.vue';
import Storage from '@/views/Settings/Storage.vue';
import commonProperties from './common-properties';

export default {
  name: 'ImageFile',
  components: {
    IconEdit,
    IconClose,
    IconCollections,
    Storage,
  },
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    maxWidth: {
      type: Number,
      default: 1920,
    },
    maxHeight: {
      type: Number,
      default: undefined,
    },
    resizeInsteadCrop: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: undefined,
    },
    mimeTypes: {
      type: Array,
      default() {
        return ['image/gif', 'image/jpeg', 'image/png'];
      },
    },
    enableRemoval: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploader: null,
      isModalOpen: false,
    };
  },
  computed: {
    handleModal() {
      return this.$route.name !== 'settings'
        ? this.openMediaStorage
        : this.openUploader;
    },
    hasImage() {
      return typeof this.model === 'string' && /\S/.test(this.model);
    },
    background() {
      if (this.hasImage) {
        return `url('${this.model}') border-box no-repeat center center / cover,
                url(${transparencyBackground}) border-box`;
      }
      return null;
    },
    isSquare() {
      return (
        this.maxWidth &&
        this.maxHeight &&
        this.maxWidth === this.maxHeight &&
        !this.resizeInsteadCrop
      );
    },
    buttonLabel() {
      if (this.hasImage) {
        return 'Alterar imagem';
      }

      return 'Adicionar imagem';
    },
    endpoint() {
      const baseUrl =
        process.env.VUE_APP_API_BASE_URL ||
        'https://api.siteletsbook.com.br/v2/';

      return `${baseUrl}storage`;
    },
    showRemovalButton() {
      return this.hasImage && this.enableRemoval;
    },
  },
  created() {
    this.uploader = new Uppload({
      lang: pt,
      uploader: xhrUploader({
        endpoint: this.endpoint,
        fileKeyName: 'file',
        settingsFunction(xmlHttp) {
          xmlHttp.setRequestHeader('Authorization', getToken());
        },
      }),
    });

    this.uploader.updateSettings({
      maxHeight: this.maxHeight,
      maxWidth: this.maxWidth,
    });

    this.uploader.use(
      new Local({
        maxFileSize: this.maxFileSize * 1024 * 1024,
        mimeTypes: this.mimeTypes,
      }),
    );

    if (!this.resizeInsteadCrop) {
      if (this.maxWidth === this.maxHeight) {
        this.uploader.use(
          new Crop({
            aspectRatio: 1,
          }),
        );
      } else {
        const aspectRatioOptions = {
          free: NaN,
          square: 1,
          '16:9': 16 / 9,
        };

        if (this.maxWidth && this.maxHeight) {
          aspectRatioOptions.Recomendada = this.maxWidth / this.maxHeight;
        }

        this.uploader.use(
          new Crop({
            aspectRatioOptions,
          }),
        );
      }
    }

    this.uploader.on('upload', (url) => {
      this.$store.dispatch('loadMediaStorage', { refresh: true });
      this.model = url;
    });
  },
  methods: {
    selectMedia(mediaUrl) {
      this.model = mediaUrl;
      this.closeStorageModal();
    },
    openMediaStorage() {
      this.isModalOpen = true;
      this.$store.commit('SET_OPEN_MEDIA_STORAGE', true);
    },
    closeStorageModal() {
      this.isModalOpen = false;
      this.$store.commit('SET_OPEN_MEDIA_STORAGE', false);
    },
    openUploader() {
      this.closeStorageModal();

      const lang = {
        ...pt,
        errors: {
          ...pt.errors,
          file_too_large: `Seu arquivo deve ter até ${this.maxFileSize} MB`,
        },
      };

      /**
       * We are setting i18n just before opening because Uppload translations
       * are global. We have a custom error message with a hardcoded value
       * (max file size in MB), otherwise a second ImageFile interface would
       * overwrite the first one translations and so on.
       */
      this.uploader.updateSettings({ lang });
      this.uploader.open();
    },
    removeImage() {
      this.model = null;
    },
  },
};
</script>

<style lang="scss">
@import '~uppload/src/styles/uppload.scss';
@import '~uppload/src/themes/light.scss';
</style>

<style scoped lang="scss">
h3 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}

.btn-edit-image {
  align-items: center;
  background-image: url('../assets/img-placeholder.png');
  background-origin: border-box;
  background-position: center center;
  background-size: cover;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 250px; // mesma proporção do 1920x768
  height: 100px;

  $button: &;

  &.is-square {
    max-width: 100px;
  }

  &__icon {
    display: none;
    padding: 8px;
    background-color: $crm-blue-primary;
    border-radius: 50%;
  }

  &:hover {
    border: 3px solid $crm-blue-primary;

    #{$button}__icon {
      display: block;
    }
  }
}

.btn-remove-image {
  background-color: transparent;
  border: 0;
  color: #e0414b;
  font-size: $size-smaller;
  padding: 1em 0;
}

.storage-modal {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba($lb-dark-gray, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .upload-btn {
    font-size: $size-normal;
    padding: 6px 46px;
    margin-right: 20px;
  }

  ::v-deep {
    .storage {
      min-width: 870px;
      max-width: 870px;
      background-color: $lb-white;
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;

      .settings-grid {
        overflow: hidden;

        &__main {
          height: 100%;
          display: flex;
          flex-direction: column;

          &__header {
            padding: 20px;
            color: $primary;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            h2 {
              margin-right: auto;
            }

            > .icon {
              color: $primary;
              margin-right: 18px;
            }

            .close {
              width: 32px;
              height: 32px;
              border-radius: 4px;
              border: 1px solid $lb-gray;
              display: flex;
              justify-content: center;
              align-items: center;
            }
          }

          .storage-content {
            padding: 0 20px 20px 20px;
          }
        }
      }
    }
  }
}
</style>
